/**
 * Generate invoice HTML programmatically without needing DOM rendering
 * This allows sending emails directly without opening the receipt modal
 */

import { formatCurrency } from './dateUtils';

export const generateInvoiceHtml = async ({
  orderDetails,
  customerInfo,
  lineItemsData,
  paymentsData,
  wigDetails = {}
}) => {
  // Get company info based on warehouse
  const getCompanyInfo = () => {
    const warehouse = orderDetails?.orderWarehouse ||
                     orderDetails?.warehouse ||
                     orderDetails?.location ||
                     'Pomona';

    switch (warehouse.toLowerCase()) {
      case 'lakewood':
        return {
          name: 'Dini Wigs',
          address: '1876 Lakewood Road',
          cityState: 'Toms River, NJ 08755',
          phone: '732.575.9941',
          website: 'WWW.DiniWigs.com'
        };
      case 'israel':
        return {
          name: 'Dini Wigs Israel',
          address: 'Hartom St 9',
          cityState: 'Jerusalem, Israel',
          phone: '+972-2-123-4567',
          website: 'WWW.DiniWigs.com'
        };
      default: // Pomona
        return {
          name: 'Dini Wigs',
          address: '18 Thiells Mount Ivy Road',
          cityState: 'Pomona, NY 10970',
          phone: '(845) 262-1010',
          website: 'WWW.DiniWigs.com'
        };
    }
  };

  const companyInfo = getCompanyInfo();

  // // Format currency
  // const formatCurrency = (amount) => {
  //   const num = parseFloat(amount || 0);
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2
  //   }).format(num);
  // };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const itemsToUse = lineItemsData?.items || [];
    const paymentsToUse = paymentsData?.payments || [];

    const totalFees = paymentsToUse.reduce((sum, payment) => {
      const ccFee = parseFloat(payment.CreditCardFee || payment.cardFee || payment.creditCardFee || 0);
      return sum + ccFee;
    }, 0);

    // Calculate subtotal from line items (excluding tax items)
    const subtotal = itemsToUse.reduce((sum, item) => {
      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
      if (description.includes('tax') || description.includes('processing fee') || description.includes('credit card fee')) {
        return sum;
      }
      const itemTotal = parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
      return sum + itemTotal;
    }, 0);

    // Find tax amount from line items
    const taxLineItems = itemsToUse.filter(item => {
      const description = (item.OrderItemDescription || item.description || '').toLowerCase();
      return description.includes('tax applied') && !description.includes('credit card') && !description.includes('processing');
    });

    const taxFromLineItems = taxLineItems.reduce((sum, item) => {
      return sum + parseFloat(item.OrderItemExtendedCost || item.extendedCost || item.price || 0);
    }, 0);

    // Analyze payments
    let regularPayments = 0;
    let taxPayments = 0;
    let creditCardFees = 0;

    paymentsToUse.forEach(payment => {
      const paymentType = (payment.PaymentType || '').toLowerCase();
      const paymentAmount = parseFloat(payment.PaymentAmount || payment.amount || 0);
      const ccFee = parseFloat(payment.CreditCardFee || payment.cardFee || payment.creditCardFee || 0);

      if (paymentType.includes('tax')) {
        taxPayments += paymentAmount;
      } else {
        regularPayments += paymentAmount;
      }

      creditCardFees += ccFee;
    });

    const totalPayments = regularPayments + taxPayments;
    const totalDue = subtotal + taxFromLineItems;
    const balanceDue = Math.max(0, totalDue - totalPayments);

    return {
      subtotal,
      taxAmount: taxFromLineItems,
      taxPayments,
      totalPayments,
      creditCardFees,
      totalDue,
      balanceDue,
      totalFees
    };
  };

  const totals = calculateTotals();
  const items = lineItemsData?.items || [];
  const payments = paymentsData?.payments || [];

  // Load logo as base64
  let logoBase64 = '';
  try {
    const logoResponse = await fetch('/images/dini_title.png');
    const logoBlob = await logoResponse.blob();
    logoBase64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(logoBlob);
    });
  } catch (error) {
    console.warn('Could not load logo:', error);
  }

  // Build the HTML
  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice - Order #${orderDetails.orderNo}</title>
  <meta charset="utf-8">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;600;700&display=swap');

    @page { margin: 0.5in; size: letter; }

    :root {
      --dini-pink: #FF68C5; --dini-pink-light: #FF98D9; --dini-pink-dark: #E91E63;
      --text-dark: #000000; --text-light: #333333; --border-light: #e0e0e0;
      --bg-white: #ffffff; --bg-light: #fafafa;
    }

    body { margin: 0 !important; padding: 0 !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; background: var(--bg-white) !important; }
    .no-print { display: none !important; }
    .receipt-container { background: var(--bg-white) !important; color: var(--text-dark) !important; font-family: 'Inter', sans-serif !important; max-width: 100% !important; margin: 0 !important; padding: 20px !important; line-height: 1.5 !important; font-size: 13px !important; position: relative !important; }
    .modern-header { display: flex !important; justify-content: space-between !important; align-items: flex-start !important; margin-bottom: 25px !important; padding-bottom: 15px !important; border-bottom: 2px solid var(--dini-pink) !important; padding: 20px !important; margin: -20px -20px 25px -20px !important; border-radius: 0 0 12px 12px !important; }
    .company-info-left { font-family: 'Inter', sans-serif !important; font-size: 11px !important; line-height: 1.4 !important; color: var(--text-dark) !important; font-weight: 400 !important; max-width: 200px !important; }
    .company-info-left strong { color: var(--text-dark) !important; font-weight: 600 !important; }
    .company-logo-right { text-align: right !important; max-width: 300px !important; }
    .company-logo-right img { max-width: 200px !important; height: auto !important; }
    .invoice-title-large { font-family: 'Playfair Display', serif !important; font-size: 42px !important; font-weight: 700 !important; color: var(--text-dark) !important; margin: 0 !important; letter-spacing: -1.2px !important; line-height: 0.85 !important; }
    .details-billing-container { display: flex !important; gap: 20px !important; margin-bottom: 20px !important; }
    .invoice-details { flex: 1 !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; line-height: 1.5 !important; background: var(--bg-light) !important; padding: 12px !important; border-radius: 6px !important; border-left: 3px solid var(--dini-pink) !important; }
    .invoice-details div { margin-bottom: 3px !important; display: flex !important; justify-content: space-between !important; }
    .invoice-details strong { color: var(--text-dark) !important; font-weight: 600 !important; }
    .bill-to-section { flex: 1 !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; line-height: 1.4 !important; background: var(--bg-white) !important; border: 1px solid var(--border-light) !important; padding: 12px !important; border-radius: 6px !important; }
    .bill-to-title { font-weight: 700 !important; margin-bottom: 8px !important; font-size: 11px !important; color: var(--text-dark) !important; letter-spacing: 0.3px !important; text-transform: uppercase !important; border-bottom: 1px solid var(--dini-pink-light) !important; padding-bottom: 4px !important; }
    .bill-to-section div:not(.bill-to-title) { margin-bottom: 3px !important; color: var(--text-dark) !important; }
    .modern-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; border-radius: 12px !important; overflow: hidden !important; box-shadow: 0 2px 8px rgba(26, 26, 26, 0.1) !important; }
    .modern-table th { background: linear-gradient(135deg, var(--dini-pink), var(--dini-pink-dark)) !important; color: white !important; padding: 12px 10px !important; font-weight: 600 !important; font-family: 'Inter', sans-serif !important; font-size: 11px !important; text-align: left !important; letter-spacing: 0.5px !important; text-transform: uppercase !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .modern-table th:last-child { text-align: right !important; }
    .modern-table td { padding: 10px !important; border-bottom: 1px solid var(--border-light) !important; font-family: 'Inter', sans-serif !important; font-size: 12px !important; vertical-align: top !important; background: white !important; color: var(--text-dark) !important; }
    .modern-table td:last-child { text-align: right !important; font-weight: 600 !important; color: var(--text-dark) !important; }
    .modern-table td:nth-child(2) { text-align: center !important; font-weight: 500 !important; }
    .modern-table td:nth-child(3) { text-align: right !important; color: var(--text-dark) !important; }
    .modern-table tbody tr:nth-child(even) { background: var(--bg-light) !important; }
    .modern-table tbody tr:last-child td { border-bottom: 2px solid var(--dini-pink) !important; }
    .totals-section-modern { margin-top: 15px !important; padding: 15px !important; background: var(--bg-light) !important; border: 1px solid var(--border-light) !important; border-radius: 8px !important; max-width: 280px !important; margin-left: auto !important; }
    .total-line-modern { display: flex !important; justify-content: space-between !important; align-items: center !important; margin-bottom: 5px !important; font-family: 'Inter', sans-serif !important; font-size: 12px !important; padding: 3px 0 !important; }
    .total-line-modern span:first-child { color: var(--text-dark) !important; font-weight: 500 !important; }
    .total-line-modern span:last-child { font-weight: 600 !important; color: var(--text-dark) !important; }
    .total-line-modern.final { font-weight: 700 !important; font-size: 14px !important; margin-top: 10px !important; padding-top: 10px !important; border-top: 2px solid var(--dini-pink) !important; color: var(--text-dark) !important; background: var(--dini-pink-light) !important; background-opacity: 0.15 !important; padding: 10px 8px !important; border-radius: 4px !important; }
    .total-line-modern.final span { color: var(--text-dark) !important; font-weight: 700 !important; }
    .warranty-section { margin: 10px 0 !important; padding: 10px !important; background: var(--bg-light) !important; border: 1px solid var(--border-light) !important; border-radius: 4px !important; font-family: 'Inter', sans-serif !important; font-size: 9px !important; line-height: 1.3 !important; text-align: center !important; position: relative !important; page-break-inside: avoid !important; }
    .warranty-title { font-family: 'Playfair Display', serif !important; font-weight: 600 !important; margin-bottom: 6px !important; font-size: 13px !important; color: var(--text-dark) !important; font-style: italic !important; margin-top: 2px !important; }
    .warranty-text { color: var(--text-dark) !important; font-style: italic !important; font-size: 11px !important; max-width: 800px !important; margin: 0 auto !important; line-height: 1.3 !important; }
  </style>
</head>
<body>
  <div class="receipt-container">
    <!-- Header -->
    <div class="modern-header">
      <div class="company-info-left">
        <strong>${companyInfo.name}</strong><br>
        ${companyInfo.address}<br>
        ${companyInfo.cityState}<br>
        ${companyInfo.phone}<br>
        ${companyInfo.website}
      </div>
      <div class="company-logo-right">
        <div class="invoice-title-large">INVOICE</div>
        ${logoBase64 ? `<img src="${logoBase64}" alt="Dini Wigs Logo" />` : ''}
      </div>
    </div>

    <!-- Invoice Details and Bill To -->
    <div class="details-billing-container">
      <div class="invoice-details">
        <div><strong>Order #:</strong> <span>${orderDetails.orderNo || ''}</span></div>
        <div><strong>Order Date:</strong> <span>${formatDate(orderDetails.orderDate)}</span></div>
        <div><strong>Due Date:</strong> <span>${formatDate(orderDetails.dueDate)}</span></div>
        <div><strong>Order Type:</strong> <span>${orderDetails.orderType || ''}</span></div>
      </div>
      <div class="bill-to-section">
        <div class="bill-to-title">BILL TO</div>
        <div><strong>${customerInfo.customerName || customerInfo.CustomerFullName || ''}</strong></div>
        <div>${customerInfo.phone || customerInfo.CustomerPhone || ''}</div>
        <div>${customerInfo.email || customerInfo.CustomerEmail || ''}</div>
        ${customerInfo.address || customerInfo.CustomerAddress ? `<div>${customerInfo.address || customerInfo.CustomerAddress}</div>` : ''}
        ${(customerInfo.city || customerInfo.CustomerCity) && (customerInfo.state || customerInfo.CustomerState) ?
          `<div>${customerInfo.city || customerInfo.CustomerCity}, ${customerInfo.state || customerInfo.CustomerState} ${customerInfo.zip || customerInfo.CustomerZipCode || ''}</div>` : ''}
      </div>
    </div>

    <!-- Line Items Table -->
    <table class="modern-table">
      <thead>
        <tr>
          <th>Description</th>
          <th>Quantity</th>
          <th>Unit Price</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        ${items.map(item => `
          <tr>
            <td>${item.OrderItemDescription || item.description || ''}</td>
            <td>${item.OrderItemQuantity || item.quantity || 1}</td>
            <td>${formatCurrency(item.OrderItemUnitCost || item.unitCost || 0)}</td>
            <td>${formatCurrency(item.OrderItemExtendedCost || item.extendedCost || 0)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <!-- Totals -->
    <div class="totals-section-modern">
      <div class="total-line-modern">
        <span>Subtotal:</span>
        <span>${formatCurrency(totals.subtotal)}</span>
      </div>
      ${totals.taxAmount > 0 ? `
      <div class="total-line-modern">
        <span>Tax:</span>
        <span>${formatCurrency(totals.taxAmount)}</span>
      </div>
      ` : ''}
      <div class="total-line-modern final">
        <span>Total Due:</span>
        <span>${formatCurrency(totals.totalDue)}</span>
      </div>
      ${totals.totalPayments > 0 ? `
      <div class="total-line-modern">
        <span>Total Payments:</span>
        <span>-${formatCurrency(totals.totalPayments)}</span>
      </div>
      ` : ''}
      <div class="total-line-modern final">
        <span>Balance Due:</span>
        <span>${formatCurrency(totals.balanceDue)}</span>
      </div>
    </div>

    <!-- Payments Table -->
    ${payments.length > 0 ? `
    <h3 style="margin-top: 30px; font-size: 16px; color: var(--text-dark);">Payment History</h3>
    <table class="modern-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Payment Method</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${payments.map(payment => `
          <tr>
            <td>${formatDate(payment.PaymentDate || payment.date)}</td>
            <td>${payment.PaymentType || payment.type || ''}</td>
            <td>${formatCurrency(payment.PaymentAmount || payment.amount || 0)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    ` : ''}

    <!-- Warranty Section -->
    <div class="warranty-section">
      <div class="warranty-title">Warranty Information</div>
      <div class="warranty-text">
        All our wigs come with a 30-day warranty covering manufacturing defects.
        Please contact us immediately if you experience any issues.
      </div>
    </div>
  </div>
</body>
</html>
  `;

  return html;
};
